import { Form, Formik } from 'formik';
import { useState } from 'react';
import { CloudUpload, Trash2 } from 'lucide-react';
import CustomTextInput from '../../../components/common/CustomTextInput';
import CustomSelect from '../../../components/common/CustomSelect';
import FileInput from '../../../components/common/FileInput';
import CustomSwitch from '../../../components/common/CustomSwitch';
import CustomButton from '../../../components/common/CustomButton/CustomButton';
import { deleteBanner, getmobileBannerConfig } from '../../../utils/urls';
import ServerPaginationDataGridWithReactQuery from '../../../components/common/CustomDataGrid/ServerPaginationDataGridWithReactQuery';
import CustomIconButton from '../../../components/common/CustomButton copy/CustomIconButton';
import { toast } from 'react-toastify';
import axios from 'axios';
import CustomDialog from '../../../components/common/CustomDialog';

const BannerAppConfig = ({
    bannerInitialValues,
    handleBannerSubmit,
    bannerValidationSchema,
}) => {
    const [uploadedFile, setUploadedFile] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [lastSubmittedFileType, setLastSubmittedFileType] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedIdToDelete, setSelectedIdToDelete] = useState(null);

    const handleDelete = (id) => {
        setSelectedIdToDelete(id);
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedIdToDelete) return;

        setIsDeleting(true);
        try {
            const url = deleteBanner.replace("{id}", selectedIdToDelete);
            await axios.delete(url);

            toast.success("Item deleted successfully.");
            setRefreshKey((prev) => prev + 1);
            setShowDeleteDialog(false);
        } catch (error) {
            console.error("Delete failed", error);
            toast.error("Failed to delete item.");
        } finally {
            setIsDeleting(false);
            setSelectedIdToDelete(null);
        }
    };


    const columns = [
        {
            field: 'title',
            headerName: 'Title',
            flex: 1,
        },
        {
            field: 'url',
            headerName: 'File',
            flex: 1.5,
            renderCell: (row) =>
                row.url ? (
                    <a href={row.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                        [Link]
                    </a>
                ) : (
                    'No File'
                ),
        },
        {
            field: 'is_active',
            headerName: 'Status',
            flex: 1,
            renderCell: (row) => (
                <div className='w-full flex justify-center align-middle'>
                    <span className={`text-xs px-2 py-1 rounded-full ${row.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {row.is_active ? 'Enable' : 'Disable'}
                    </span>
                </div>
            ),
        },
        {
            headerName: "Action",
            width: 100,
            renderCell: (row) => (
                <div className="w-full flex justify-center items-center">
                    <CustomIconButton
                        type="button"
                        variant="text"
                        size="sm"
                        icon={<Trash2 size={18} color='red' />}
                        onClick={() => handleDelete(row.id)}
                    />
                </div>
            ),
        },
    ];

    return (
        <div>
            <Formik
                initialValues={bannerInitialValues}
                validationSchema={bannerValidationSchema}
                onSubmit={async (values, actions) => {
                    try {
                        await handleBannerSubmit(values);
                        actions.resetForm();
                        setUploadedFile(null);
                        setLastSubmittedFileType(values.fileType);
                        setRefreshKey((prev) => prev + 1);
                    } catch (error) {
                        console.error(error.message);
                    } finally {
                        actions.setSubmitting(false);
                    }
                }}
            >
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form className="space-y-8 mb-10 border rounded-lg p-6 shadow-sm bg-white">
                        <h2 className="text-xl font-semibold text-blue-900 mb-6">Banner</h2>

                        <div className="grid md:grid-cols-6 gap-6 mb-6 items-end">
                            <div className="col-span-2">
                                <CustomTextInput
                                    required
                                    name="title"
                                    label="Banner Title"
                                    placeholder="Enter Banner Title"
                                />
                            </div>

                            <div className="col-span-4 grid grid-cols-2 gap-4">
                                <CustomSelect
                                    name="fileType"
                                    label="File Type"
                                    required
                                    menuList={[
                                        { label: 'Image', value: 'image' },
                                        { label: 'Video', value: 'video' },
                                    ]}
                                    placeholder="Select media type"
                                    onChange={(e) => {
                                        const type = e.target.value;
                                        setFieldValue('fileType', type);
                                        setFieldValue('file', null);
                                        setUploadedFile(null);
                                    }}
                                />

                                <FileInput
                                    name="file"
                                    fileType={values.fileType}
                                    label={<CloudUpload className="w-5 h-5 text-blue-500" />}
                                    inputLabel={`Upload ${values.fileType === 'video' ? 'Video' : 'Image'}`}
                                    accept={values.fileType === 'video' ? 'video/*' : 'image/*'}
                                    variant="traditional"
                                    buttonVariant="outlined"
                                    setSelectedFiles={(files) => {
                                        const file = files?.[0];
                                        if (file instanceof File) {
                                            setFieldValue('file', file);
                                            setUploadedFile(file);
                                        } else {
                                            setFieldValue('file', null);
                                            setUploadedFile(null);
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-start">
                            <div className="col-span-5">
                                <CustomTextInput
                                    name="description"
                                    label="Description"
                                    placeholder="Enter description"
                                />
                            </div>

                            <div className="col-span-1 grid gap-3">
                                <label className="text-sm font-medium text-gray-700">Status</label>
                                <div className="flex items-center gap-2">
                                    <CustomSwitch
                                        checked={values.enabled}
                                        onChange={(newValue) => setFieldValue('enabled', newValue)}
                                        value={values.enabled}
                                        name="enabled"
                                    />
                                    <span className="text-sm">
                                        {values.enabled ? 'Enable' : 'Disable'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-2 pt-2">
                                    <CustomButton type="reset" label="Clear" variant="outlined" disabled={isSubmitting} />
                                    <CustomButton
                                        type="submit"
                                        label="Add"
                                        variant="contained"
                                        loading={isSubmitting}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>

            <div className="mt-6">
                <ServerPaginationDataGridWithReactQuery
                    key={refreshKey}
                    url={getmobileBannerConfig}
                    limit={10}
                    keyId="id"
                    isServerPagination
                    columns={columns}
                    dataMapper={(res) => {
                        console.log("Check this log : ", res);

                        return (
                            res?.data?.map((item) => ({
                                id: item.id,
                                title: item.title,
                                url: item.url,
                                is_active: item.is_active,
                                description: item.description,
                                created_at: item.created_at,
                            })) || []
                        );
                    }}
                />
            </div>

            <CustomDialog
                open={showDeleteDialog}
                setOpen={setShowDeleteDialog}
                title="Confirm Delete"
                showClose={true}
            >
                <div className="p-5">
                    <p className="text-base mb-4">
                        Do you really want to delete this banner?
                    </p>
                    <div className="flex justify-end gap-3">
                        <CustomButton
                            variant="outlined"
                            label="Cancel"
                            onClick={() => setShowDeleteDialog(false)}
                        />
                        <CustomButton
                            variant="error"
                            label={
                                isDeleting ? (
                                    <div className="flex items-center gap-2">
                                        <span>Deleting</span>
                                        <span className="animate-spin rounded-full border-2 border-white border-t-transparent h-4 w-4" />
                                    </div>
                                ) : (
                                    "Yes, Delete"
                                )
                            }
                            disabled={isDeleting}
                            onClick={confirmDelete}
                        />
                    </div>
                </div>
            </CustomDialog>
        </div>
    );
};

export default BannerAppConfig;
